import { Check } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { OnboardingLabels, WizardStep } from "./types";

export interface OnboardingStepIndicatorProps {
  current: WizardStep;
  labels: OnboardingLabels;
  onStepClick?: (step: WizardStep) => void;
  className?: string;
}

const STEPS: WizardStep[] = [1, 2, 3];

export function OnboardingStepIndicator({ current, labels, onStepClick, className }: OnboardingStepIndicatorProps) {
  return (
    <ol
      role="list"
      aria-label="Onboarding steps"
      className={cn(
        "flex w-full items-stretch gap-2 overflow-x-auto pb-1 sm:gap-3",
        "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      {STEPS.map((step, idx) => {
        const status: "done" | "current" | "pending" = step < current ? "done" : step === current ? "current" : "pending";
        const stepLabel = labels.steps[step];
        const interactive = onStepClick && status !== "pending";

        const Tag = interactive ? "button" : "div";

        return (
          <li key={step} className="flex flex-1 min-w-[10rem] items-stretch">
            <Tag
              type={interactive ? "button" : undefined}
              onClick={interactive ? () => onStepClick?.(step) : undefined}
              aria-current={status === "current" ? "step" : undefined}
              aria-label={`${stepLabel.title} — ${labels.stepStatus[status]}`}
              className={cn(
                "group flex w-full items-center gap-3 rounded-biz-card border px-3 py-2.5 text-left transition-colors duration-150",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-offset-2",
                status === "current" && "border-biz-primary bg-biz-primary/5",
                status === "done" && "border-biz-success/40 bg-biz-success/5",
                status === "pending" && "border-biz-border bg-biz-surface",
                interactive && "cursor-pointer hover:border-biz-primary/60",
              )}
            >
              <span
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums",
                  status === "current" && "bg-biz-primary text-biz-primary-foreground ring-2 ring-biz-primary/20",
                  status === "done" && "bg-biz-success text-biz-success-foreground",
                  status === "pending" && "bg-biz-muted text-biz-foreground-muted",
                )}
                aria-hidden
              >
                {status === "done" ? <Check size={16} /> : step}
              </span>
              <span className="flex min-w-0 flex-col">
                <span
                  className={cn(
                    "truncate text-sm font-semibold leading-tight",
                    status === "current" ? "text-biz-primary" : status === "done" ? "text-biz-foreground" : "text-biz-foreground-muted",
                  )}
                >
                  {stepLabel.title}
                </span>
                <span className="truncate text-xs text-biz-foreground-muted">{stepLabel.description}</span>
              </span>
            </Tag>
            {idx < STEPS.length - 1 && (
              <div
                aria-hidden
                className={cn(
                  "mx-1 hidden h-px w-6 self-center sm:block",
                  step < current ? "bg-biz-success/60" : "bg-biz-border",
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
